/**
 * SSO status adapter for the shared ACS Bootstrap bundles. Advisory UI
 * personalization only — never auth, entitlement, or access control.
 *
 * Bootstrap supports three consumer modes. The SSO Status Service is only
 * called as a last-resort backup (see mode 3):
 *
 * 1. Cookie-backed rich status — ACS.org/AEM and selected consumers such as My
 *    Account. An `acs_user_status` cookie means the browser established status
 *    through the ACS.org/AEM login path. Carries membership, package and
 *    renewal attributes plus the legacy compatibility fields.
 * 2. Application-provided authentication status — eWeb applications that
 *    migrated to Okta independently and choose to keep limited header
 *    personalization. The host supplies `window.isLoggedIn` and
 *    `window.userDisplayName`, and Bootstrap builds a deliberately limited user
 *    flagged `membershipKnown: false`. Supported UI is authentication-level
 *    only, such as Log In versus Welcome / Log Out. Membership, package and
 *    renewal state must never be inferred on this path.
 * 3. Status Service backup — hosts that supply neither. Bootstrap falls back to
 *    the SSO Status Service request (defined in requests.js), and resolves
 *    anonymous if that request fails.
 *
 * The host contract selects the mode: declaring `isLoggedIn` at all, even as
 * `false`, opts into mode 2 and the cookie is never read. Otherwise the cookie
 * is read. A cookie that explicitly says logged out is honoured; any other
 * unusable cookie (missing, malformed, expired, ...) is mode 3.
 *
 * Public surface is unchanged. `acs.sso.getStatus()` resolves `{ succ, results }`
 * and carries `.always()`, `acs.user` mirrors the current status, and
 * `sso.status.changed` is published only when the status actually changes.
 */
import { amplify } from './acs-amplify.js';
import { debugit } from './acs-debug.js';
import deepEqual from './utils/deep-equals';
import { PARSE_STATUS, anonymousUser, readUserStatus, toCompatibilityUser } from './user-status-cookie.js';

export function ssoModule() {
  if (typeof acs === 'undefined') {
    window.acs = { sso: {}, user: {} };
  }
  window.acs.sso = window.acs.sso || {};

  /** Mode 2 is selected by the host declaring the global, even when logged out. */
  function hostProvidesAuthStatus() {
    return typeof isLoggedIn !== 'undefined';
  }

  function applyUser(user, publishedSucc = true) {
    const previous = { ...window.acs.user };
    window.acs.user = user;

    // Only publish when the user state meaningfully changed, so repeated
    // getStatus() calls and re-entrant handlers cannot loop.
    if (!deepEqual(previous, user)) {
      amplify.publish('sso.status.changed', user, publishedSucc);
    }
  }

  function withAlways(data) {
    const promise = Promise.resolve(data);
    promise.always = function (callback) {
      promise.then(callback).catch(callback);
      return promise;
    };
    return promise;
  }

  /**
   * Maps a Status Service envelope, either from the mode 3 backup request or
   * published on the raw `sso.status` topic.
   */
  function mapLegacyStatus(data = {}) {
    if (!data.succ || !data.results) {
      return { loggedin: false };
    }

    const user = { ...data.results };
    if (user.firstname && user.lastname) {
      user.fullname = user.firstname + ' ' + user.lastname;
    }
    if (user.membertype) {
      user.communitymember = user.membertype === 'COMMUNITY_ASSOCIATE' || user.membertype === 'COMMUNITY_MEMBER';
    }
    user.haspackage = user.packagecode === 'BASIC' || user.packagecode === 'PREMIUM' || user.packagecode === 'STANDARD';
    return user;
  }

  /** The cookie can supply a user, either logged in or explicitly logged out. */
  function cookieProvidesStatus(parseResult) {
    return parseResult.status === PARSE_STATUS.OK || parseResult.status === PARSE_STATUS.NOT_LOGGED_IN;
  }

  /**
   * Mode 3. Last-resort backup when neither host vars nor a usable cookie are
   * available: hits the SSO Status Service. Resolves `{ succ, results }` and
   * rejects on failure, matching the original getStatus() contract.
   */
  function getStatusFromService(isCached) {
    const promise = new Promise((resolve, reject) => {
      amplify.request({
        resourceId: isCached ? 'sso.status' : 'sso.status.noCache',
        success: function (data) {
          applyUser(mapLegacyStatus(data), Boolean(data?.succ));
          resolve(data);
        },
        error: function (data, status) {
          applyUser(anonymousUser(), false);

          if (status === 'abort') {
            reject('could not connect to sso.getStatus service');
          } else {
            reject('sso.getStatus returned an error');
          }
        },
      });
    });

    promise.always = function (callback) {
      promise.then(callback).catch(callback);
      return promise;
    };
    return promise;
  }

  /**
   * Mode 2. Builds the user from the login vars rendered onto the page
   * (window.isLoggedIn / window.userDisplayName). Membership and renewal state is
   * unknown here, so it is flagged `membershipKnown: false` and the UI hides
   * anything that depends on it. `succ` tracks login state on this path.
   */
  window.acs.sso.initFromVars = function () {
    const loggedin = !!window.isLoggedIn;
    const displayName = loggedin ? window.userDisplayName || null : null;

    applyUser(
      {
        loggedin: loggedin,
        fullname: displayName || undefined,
        membershipKnown: false, // no membership/renewal data available from these vars
      },
      loggedin,
    );

    return withAlways({ succ: loggedin, results: window.acs.user });
  };

  /**
   * Re-reads the host contract on every call, so a mode 2 host that calls
   * getStatus() after init() keeps its host-provided user instead of being reset
   * to anonymous by a cookie that will never exist on its domain.
   *
   * Order: host vars, then the cookie, then the Status Service as a last resort.
   *
   * `isCached` only applies to the Status Service backup; neither the host vars
   * nor the cookie involve a network request.
   *
   * On the host var and cookie paths, mapping, assignment and event publication
   * all happen during this call; the returned Promise still settles through
   * normal microtask scheduling.
   */
  window.acs.sso.getStatus = function (isCached = true) {
    if (hostProvidesAuthStatus()) {
      return window.acs.sso.initFromVars();
    }

    const cookieStatus = readUserStatus();
    if (cookieProvidesStatus(cookieStatus)) {
      applyUser(toCompatibilityUser(cookieStatus));
      return withAlways({ succ: true, results: window.acs.user });
    }

    debugit('SSO: no host login vars or usable acs_user_status cookie, falling back to SSO status service');
    return getStatusFromService(isCached);
  };

  window.acs.sso.init = function () {
    // Host-provided login vars take precedence. If they exist at all, even
    // logged-out (isLoggedIn=false, userDisplayName=null), use them.
    if (hostProvidesAuthStatus()) {
      debugit('SSO: host-provided authentication status (isLoggedIn/userDisplayName), membership state unknown');
      return window.acs.sso.initFromVars();
    }

    debugit('SSO: no host login vars found, trying acs_user_status cookie then SSO status service');

    // Seed the anonymous user only when the cookie cannot supply a logged-in
    // one. Seeding unconditionally would stomp a usable cookie-derived user, and
    // would make the getStatus() below publish a redundant anonymous change event.
    if (readUserStatus().status !== PARSE_STATUS.OK) {
      window.acs.user = anonymousUser();
    }

    // Listen to the sso status change, and retransmit after mutating the acs variable
    amplify.subscribe('sso.status', function (newData) {
      applyUser(mapLegacyStatus(newData), Boolean(newData?.succ));
    });

    return window.acs.sso.getStatus();
  };
}
ssoModule();
